import type { Broadcast, BroadcastStatus } from "@/features/broadcast/types";
import type {
  BroadcastAnswered,
  BroadcastNamedCount,
  BroadcastNotSentReason,
} from "@/features/reports/broadcasts/types";

import { DateTime } from "luxon";

import { getMockMarketProfile } from "../market";

import {
  BROADCAST_PAUSE_CATEGORY,
  BROADCAST_STATUS,
} from "@/features/broadcast/constants";

/** One of the broadcast page's boards: its row here, its figures in the report mock. */
export interface BroadcastPageSeed {
  id: string;
  name: string;
  status: BroadcastStatus;
  isStore?: boolean;
  segmentName: string;
  templateName: string;
  channelName: string;
  createdBy: string;
  targeted: number;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  booked: number;
  newPatients: number;
  attended: number;
  upcoming: number;
  optedOut: number;
  notSent: number;
  spent: number;
  collected: number;
  startedAt: string | null;
  completedAt: string | null;
  scheduledAt: string | null;
  pausedAt: string | null;
  resumesAt: string | null;
  createdAt: string;
  /** Local "HH:mm" sending hours. */
  hours: [string, string];
  /** Messages a minute. */
  pace: number;
  notSentBy: Partial<Record<BroadcastNotSentReason, number>>;
  treatments: BroadcastNamedCount[];
  doctors: BroadcastNamedCount[];
  answered?: BroadcastAnswered;
  /** Replies reached by each hour of the report's curve, out of 100. */
  curve?: number[];
  spend?: { market: string; count: number; cost: number }[];
  /** Named rows, by their place in the people list. */
  people?: Record<
    number,
    { name: string; phone?: string; reply?: string; isNewPatient?: boolean }
  >;
}

export interface GenerateBroadcastsInput {
  organizationId: string;
  workspaceId: string;
  channelId: string;
  segmentIds: string[];
  isStore: boolean;
}

/** The channel's fourth mock template: a text header with a URL and a quick reply. */
export const MOCK_BROADCAST_TEMPLATE_ID = "template-4";

// Board clocks are the workspace's, so the seeds are built in the market's zone.
const at = (daysAgo: number, hour: number, minute = 0) =>
  DateTime.now()
    .setZone(getMockMarketProfile().timezone)
    .minus({ days: daysAgo })
    .set({ hour, minute, second: 0, millisecond: 0 })
    .toISO() ?? "";

const minutesAgo = (minutes: number) =>
  DateTime.now().minus({ minutes }).toISO() ?? "";

const CLINIC_HOURS: [string, string] = ["09:00", "20:30"];

const CLINIC_SEEDS: BroadcastPageSeed[] = [
  {
    id: "bc-ramona",
    name: "Ramonaa Group 2",
    status: BROADCAST_STATUS.COMPLETED,
    segmentName: "Ramona Group 2",
    templateName: "ramona_joining",
    channelName: "Storax Lifestyle Clinic",
    createdBy: "Gaurav",
    targeted: 220,
    sent: 153,
    delivered: 147,
    read: 72,
    replied: 3,
    booked: 1,
    newPatients: 1,
    attended: 0,
    upcoming: 1,
    optedOut: 0,
    notSent: 67,
    spent: 22.53,
    collected: 0,
    startedAt: at(8, 12, 15),
    completedAt: at(8, 12, 41),
    scheduledAt: null,
    pausedAt: null,
    resumesAt: null,
    createdAt: at(16, 9),
    hours: CLINIC_HOURS,
    pace: 6,
    notSentBy: {
      not_on_whatsapp: 41,
      turned_off_marketing: 14,
      frequency_cap: 9,
      other: 3,
    },
    treatments: [{ name: "Consultation", count: 1 }],
    doctors: [{ name: "Dr. Ramona Haddad", count: 1 }],
    answered: {
      echo: { count: 2, medianSeconds: 38 },
      team: { count: 0, medianSeconds: null },
      nobody: 1,
    },
    curve: [0, 33, 67, 67, 67, 67, 100, 100, 100],
    spend: [
      { market: "United Arab Emirates", count: 140, cost: 19.73 },
      { market: "Other", count: 5, cost: 1.11 },
      { market: "Saudi Arabia", count: 2, cost: 1.01 },
      { market: "India", count: 6, cost: 0.68 },
    ],
    people: {
      0: {
        name: "Dana Barghouti",
        phone: "+974 5579 7978",
        reply: "Is Dr. Ramona taking new patients?",
        isNewPatient: true,
      },
      1: { name: "Mona", reply: "What are her hours on Saturdays?" },
      2: {
        name: "Latifa Al Nuaimi",
        reply: "Congrats to her! Will book next month",
      },
      3: { name: "Badour" },
      4: { name: "Shahad Al Qallaf" },
    },
  },
  {
    id: "bc-whitening-october",
    name: "Teeth whitening, October",
    status: BROADCAST_STATUS.SCHEDULED,
    segmentName: "Whitening leads",
    templateName: "whitening_offer",
    channelName: "Storax Lifestyle Clinic",
    createdBy: "Anjali",
    targeted: 482,
    sent: 0,
    delivered: 0,
    read: 0,
    replied: 0,
    booked: 0,
    newPatients: 0,
    attended: 0,
    upcoming: 0,
    optedOut: 0,
    notSent: 0,
    spent: 0,
    collected: 0,
    startedAt: null,
    completedAt: null,
    scheduledAt: at(-2, 10),
    pausedAt: null,
    resumesAt: null,
    createdAt: at(3, 15, 20),
    hours: CLINIC_HOURS,
    pace: 6,
    notSentBy: {},
    treatments: [],
    doctors: [],
  },
  {
    id: "bc-skincare",
    name: "New arrivals, skincare",
    status: BROADCAST_STATUS.SENDING,
    segmentName: "Skincare buyers",
    templateName: "skincare_arrivals",
    channelName: "Storax Lifestyle Clinic",
    createdBy: "Anjali",
    targeted: 389,
    sent: 233,
    delivered: 210,
    read: 41,
    replied: 2,
    booked: 0,
    newPatients: 0,
    attended: 0,
    upcoming: 0,
    optedOut: 0,
    notSent: 7,
    spent: 33.1,
    collected: 0,
    startedAt: minutesAgo(40),
    completedAt: null,
    scheduledAt: null,
    pausedAt: null,
    resumesAt: null,
    createdAt: at(1, 11),
    hours: CLINIC_HOURS,
    pace: 6,
    notSentBy: { not_on_whatsapp: 7 },
    treatments: [],
    doctors: [],
  },
  {
    id: "bc-hydrafacial-paused",
    name: "Hydrafacial, 20% off",
    status: BROADCAST_STATUS.PAUSED,
    segmentName: "Facial leads",
    templateName: "hydrafacial_offer",
    channelName: "Storax Lifestyle Clinic",
    createdBy: "Anjali",
    targeted: 389,
    sent: 142,
    delivered: 136,
    read: 58,
    replied: 4,
    booked: 1,
    newPatients: 1,
    attended: 0,
    upcoming: 1,
    optedOut: 1,
    notSent: 0,
    spent: 20.4,
    collected: 0,
    startedAt: at(1, 19, 50),
    completedAt: null,
    scheduledAt: null,
    pausedAt: at(1, 20, 30),
    resumesAt: at(-1, 9),
    createdAt: at(2, 10),
    hours: CLINIC_HOURS,
    pace: 6,
    notSentBy: {},
    treatments: [{ name: "Hydrafacial", count: 1 }],
    doctors: [{ name: "Dr. Lina Farouk", count: 1 }],
  },
  {
    id: "bc-hydrafacial-cancelled",
    name: "Hydrafacial, 20% off",
    status: BROADCAST_STATUS.CANCELLED,
    segmentName: "Facial leads",
    templateName: "hydrafacial_offer",
    channelName: "Storax Lifestyle Clinic",
    createdBy: "Anjali",
    targeted: 389,
    sent: 142,
    delivered: 138,
    read: 58,
    replied: 4,
    booked: 1,
    newPatients: 1,
    attended: 1,
    upcoming: 0,
    optedOut: 1,
    notSent: 0,
    spent: 20.4,
    collected: 1200,
    startedAt: at(2, 19, 50),
    completedAt: at(2, 20, 41),
    scheduledAt: null,
    pausedAt: null,
    resumesAt: null,
    createdAt: at(3, 10),
    hours: CLINIC_HOURS,
    pace: 6,
    notSentBy: {},
    treatments: [{ name: "Hydrafacial", count: 1 }],
    doctors: [{ name: "Dr. Lina Farouk", count: 1 }],
  },
];

const STORE_SEEDS: BroadcastPageSeed[] = [
  {
    id: "bc-eid",
    name: "Eid gift sets, 15% off",
    status: BROADCAST_STATUS.COMPLETED,
    isStore: true,
    segmentName: "Gift buyers, last Eid",
    templateName: "eid_gift_sets",
    channelName: "Maison Oud",
    createdBy: "Layla",
    targeted: 1240,
    sent: 1118,
    delivered: 1093,
    read: 702,
    replied: 54,
    booked: 23,
    newPatients: 0,
    attended: 0,
    upcoming: 0,
    optedOut: 6,
    notSent: 122,
    spent: 164.7,
    collected: 6840,
    startedAt: at(3, 11),
    completedAt: at(3, 12, 52),
    scheduledAt: null,
    pausedAt: null,
    resumesAt: null,
    createdAt: at(6, 9),
    hours: ["10:00", "22:00"],
    pace: 10,
    notSentBy: {
      not_on_whatsapp: 71,
      turned_off_marketing: 28,
      frequency_cap: 19,
      other: 4,
    },
    treatments: [],
    doctors: [],
    answered: {
      echo: { count: 41, medianSeconds: 29 },
      team: { count: 9, medianSeconds: 360 },
      nobody: 4,
    },
    spend: [
      { market: "United Arab Emirates", count: 1040, cost: 146.54 },
      { market: "Saudi Arabia", count: 34, cost: 11.7 },
      { market: "Other", count: 44, cost: 6.46 },
    ],
    people: {
      0: {
        name: "Lina Farah",
        phone: "+971 50 331 8204",
        reply: "Ordered the large set, thank you!",
      },
      1: {
        name: "Sara Al Marri",
        reply: "Do you have the oud and amber set in the large size?",
      },
      2: { name: "Fatima Hassan", reply: "SHOP" },
      3: {
        name: "Noura Khalifa",
        reply: "Is the delivery free to Sharjah too?",
      },
    },
  },
];

export const BROADCAST_PAGE_SEEDS = [...CLINIC_SEEDS, ...STORE_SEEDS];

/** A local "HH:mm" as the UTC "HH:mm" a broadcast's hours are stored in. */
const utcClock = (local: string) => {
  const [hour, minute] = local.split(":").map(Number);

  return DateTime.now().set({ hour, minute }).toUTC().toFormat("HH:mm");
};

export function generateBroadcasts({
  organizationId,
  workspaceId,
  channelId,
  segmentIds,
  isStore,
}: GenerateBroadcastsInput): Broadcast[] {
  return BROADCAST_PAGE_SEEDS.filter(
    (seed) => Boolean(seed.isStore) === isStore,
  ).map((seed, index) => ({
    id: seed.id,
    name: seed.name,
    channelId,
    segmentId: segmentIds[index % segmentIds.length] ?? "",
    templateId: MOCK_BROADCAST_TEMPLATE_ID,
    variables: { body: [{ type: "text", text: "Hi {{1}}" }] },
    status: seed.status,
    scheduledAt: seed.scheduledAt,
    startedAt: seed.startedAt,
    completedAt: seed.completedAt,
    totalContacts: seed.targeted,
    sentCount: seed.sent,
    deliveredCount: seed.delivered,
    readCount: seed.read,
    failedCount: seed.notSent,
    interactedOnly: false,
    activeHours: [
      { start: utcClock(seed.hours[0]), end: utcClock(seed.hours[1]) },
    ],
    config: { rateLimit: seed.pace, rateDuration: 60 },
    createdBy: null,
    organizationId,
    workspaceId,
    pausedAt: seed.pausedAt,
    pauseCategory:
      seed.status === BROADCAST_STATUS.PAUSED
        ? BROADCAST_PAUSE_CATEGORY.OUTSIDE_ACTIVE_HOURS
        : null,
    pauseDescription: null,
    resumesAt: seed.resumesAt,
    createdAt: seed.createdAt,
    updatedAt: seed.completedAt ?? seed.createdAt,
  }));
}
